import React, { useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";

const errMsg = (e) => e?.response?.data?.detail || e.message;

const ProjectRow = ({ p, cohorts, isAdmin, onChange }) => {
  const patch = (body) => api.patch(`/asana/projects/${p.id}`, body).then(onChange).catch((e) => toast.error("Update failed", { description: errMsg(e) }));
  const remove = () => {
    if (!window.confirm(`Stop syncing "${p.name || p.gid}"? Properties already linked stay off target lists.`)) return;
    api.delete(`/asana/projects/${p.id}`).then(() => { toast.success("Project removed"); onChange(); })
      .catch((e) => toast.error("Remove failed", { description: errMsg(e) }));
  };
  return (
    <div className="flex flex-wrap items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-0 text-xs" data-testid={`asana-project-row-${p.gid}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="font-semibold text-sm" data-testid={`asana-project-name-${p.gid}`}>{p.name || "Name loads on next sync"}</div>
        <div className="font-mono text-slate-500">{p.gid}{p.last_synced_at ? ` · ${p.last_matched}/${p.last_tasks} matched` : " · not synced yet"}</div>
      </div>
      <select value={p.cohort} disabled={!isAdmin} onChange={(e) => patch({ cohort: e.target.value })}
        className="border border-slate-300 px-2 py-1 text-xs bg-white" data-testid={`asana-project-cohort-${p.gid}`}>
        {Object.entries(cohorts).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <label className="flex items-center gap-2">
        <Switch checked={p.enabled} disabled={!isAdmin} onCheckedChange={(v) => patch({ enabled: v })} data-testid={`asana-project-enabled-${p.gid}`} />
        {p.enabled ? "Syncing" : "Paused"}
      </label>
      {isAdmin && (
        <button type="button" onClick={remove} className="text-red-700" data-testid={`asana-project-remove-${p.gid}`}><Trash2 className="h-4 w-4" /></button>
      )}
    </div>
  );
};

const AddProject = ({ cohorts, onAdded }) => {
  const [ref, setRef] = useState("");
  const [cohort, setCohort] = useState(Object.keys(cohorts)[0]);
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/asana/projects", { project: ref, cohort });
      toast.success("Asana project added", { description: data.name || data.gid });
      setRef("");
      onAdded();
    } catch (err) {
      toast.error("Couldn't add project", { description: errMsg(err) });
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={add} className="flex flex-wrap gap-2 mt-3" data-testid="asana-add-project-form">
      <input value={ref} onChange={(e) => setRef(e.target.value)} required placeholder="Paste Asana project link or ID"
        className="flex-1 min-w-[220px] border border-slate-300 px-2 py-1.5 text-xs" data-testid="asana-add-project-input" />
      <select value={cohort} onChange={(e) => setCohort(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-xs bg-white" data-testid="asana-add-project-cohort">
        {Object.entries(cohorts).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <Button type="submit" disabled={busy || !ref.trim()} className="rounded-none h-8 text-xs" data-testid="asana-add-project-btn">
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />} Add project
      </Button>
    </form>
  );
};

export const AsanaProjectsPanel = ({ projects, cohorts, isAdmin, onChange }) => (
  <div className="mt-4" data-testid="asana-projects-panel">
    <div className="text-xs font-semibold mb-1">Synced projects ({projects.length})</div>
    <div className="border border-slate-200" data-testid="asana-projects-list">
      {projects.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground" data-testid="asana-projects-empty">No Asana projects added yet.</div>}
      {projects.map((p) => <ProjectRow key={p.id} p={p} cohorts={cohorts} isAdmin={isAdmin} onChange={onChange} />)}
    </div>
    {isAdmin && <AddProject cohorts={cohorts} onAdded={onChange} />}
  </div>
);
